import { DEFAULT_PERMISSIONS } from '@/constants/constants';
import { Component, OnInit } from '@angular/core';
import { PermissionsService } from '@services/permissions.service';

type ConfigTab = 'ticket' | 'fsr' | 'string' | 'panel';

/**
 * Merged Configurations page: one menu entry (/configurations) hosting
 * Ticket/FSR/String/Panel Configuration as tabs, instead of four separate
 * menu items. Permissions are resolved once here (off /configurations) and
 * passed down to each tab via [permissionsOverride] — the Ticket/FSR tabs
 * would otherwise self-resolve off the wrong URL under this shared route.
 * The original standalone /help-desk/configuration and
 * /help-desk/fsr-configuration pages are untouched and keep working.
 */
@Component({
  selector: 'app-configurations',
  templateUrl: './configurations.component.html',
  styleUrls: ['./configurations.component.scss'],
})
export class ConfigurationsComponent implements OnInit {
  readonly tabs: { key: ConfigTab; label: string }[] = [
    { key: 'ticket', label: 'Ticket Configuration' },
    { key: 'fsr', label: 'FSR Configuration' },
    { key: 'string', label: 'String Configuration' },
    { key: 'panel', label: 'Panel Configuration' },
  ];
  activeTab: ConfigTab = 'ticket';

  permissions = { ...DEFAULT_PERMISSIONS };
  permissionsReady = false;
  loader = false;

  constructor(private permService: PermissionsService) {}

  ngOnInit(): void {
    this.initComponent();
  }

  async initComponent(): Promise<void> {
    this.loader = true;
    try {
      const url = this.permService.getCurrentUrl();
      const menuFromLocal = await this.permService.waitForMenuInLocalStorage();
      const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);

      this.permissions = matchedMenu
        ? {
            canAdd: !!matchedMenu.canAdd,
            canEdit: !!matchedMenu.canEdit,
            canView: !!matchedMenu.canView,
            canDelete: !!matchedMenu.canDelete,
          }
        : { ...DEFAULT_PERMISSIONS };
    } finally {
      this.permissionsReady = true;
      this.loader = false;
    }
  }

  selectTab(tab: ConfigTab): void {
    this.activeTab = tab;
  }
}
